import {
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonRow,
} from "@ionic/react";
import PageHeader from "../components/PageHeader";
import { useHistory, useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { useContext, useEffect, useState } from "react";
import { ColumnsWithGroupData, getColumnsWithGroup } from "../api/Windows";
import {
  GetColumnsWithGroupField,
  GetColumnsWithGroupResponse,
} from "../models/windows/GetColumnsWithGroup";
import { getEquipment } from "../api/EquipmentMasters";
import { Equipment } from "../models/equipment/Equipment";
import { getAllFields } from "../models/workorders/WorkOrder";
import { getLinkedEquipmentInspections } from "../api/EquipmentInspections";
import {
  getUserRequesterAccess,
  getUserTechnicianAccess,
  getQuickWorkOrderEmergencyAccess,
  getQuickWorkOrderRoutineAccess,
  getEquipmentHierarchyInquiryAccess,
  getEquipmentDocumentAccess,
} from "../util/UserHelper";
import ApiError from "../components/ApiError";
import {
  getCreateWorkOrderRoute,
  getOpenWorkOrdersRoute,
} from "../util/EquipmentHelper";
import { TranslationsContext } from "../util/Translations";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import type { LinkedInspection } from "../api/WorkOrders";

const EquipmentOverview: React.FC = () => {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const history = useHistory();
  const displayEquipment = decodeParam(equipmentId);
  const [columns, setColumns] = useState<GetColumnsWithGroupResponse>();
  const [equipment, setEquipment] = useState<Equipment>();
  const [fields, setFields] = useState<GetColumnsWithGroupField[]>();
  const [showLoading, setShowLoading] = useState(false);
  const [isRequester, setIsRequester] = useState(false);
  const [errorData, setErrorData] = useState("");
  const [hasCreateWorkOrderAccess, setHasCreateWorkOrderAccess] =
    useState(false);
  const [hasHierarchyAccess, setHasHierarchyAccess] = useState(false);
  const [hasDocumentAccess, setHasDocumentAccess] = useState(false);
  const [hasLinkedInspections, setHasLinkedInspections] = useState(false);
  const [linkedInspections, setLinkedInspections] = useState<LinkedInspection[]>(
    []
  );
  const { translations } = useContext(TranslationsContext);

  useEffect(() => {
    setup();
  }, []);

  useEffect(() => {
    if (columns) {
      const fields = getAllFields(columns);
      setFields(fields as GetColumnsWithGroupField[]);
    }
  }, [columns]);

  const getColumns = async () => {
    const data: ColumnsWithGroupData = {
      WindowName: "mob_equipment",
      IncludeValidValues: "true",
    };
    const response = await getColumnsWithGroup(data);
    return response.data as GetColumnsWithGroupResponse;
  };

  const getEquipmentDetails = async (name: string) => {
    const response = await getEquipment({ equipment: name });
    if (response.isError) {
      setErrorData(response.data?.toString());
    }
    return response.data as Equipment;
  };

  const setup = async () => {
    setShowLoading(true);

    const [columns, equipment] = await Promise.all([
      getColumns(),
      getEquipmentDetails(displayEquipment),
    ]);
    setColumns(columns);
    setEquipment(equipment);

    if (
      !(await getUserTechnicianAccess()) &&
      (await getUserRequesterAccess())
    ) {
      setIsRequester(true);
    }

    const [
      hasEmergencyAccess,
      hasRoutineAccess,
      hierarchyAccess,
      documentAccess,
    ] = await Promise.all([
      getQuickWorkOrderEmergencyAccess(),
      getQuickWorkOrderRoutineAccess(),
      getEquipmentHierarchyInquiryAccess(),
      getEquipmentDocumentAccess(),
    ]);

    setHasCreateWorkOrderAccess(hasEmergencyAccess || hasRoutineAccess);
    setHasHierarchyAccess(hierarchyAccess);
    setHasDocumentAccess(documentAccess);
    try {
      const linkedInspectionsResponse = await getLinkedEquipmentInspections({
        equipSerialId: displayEquipment,
        equipSerialInd: "e",
      });
      if (
        !linkedInspectionsResponse.isError &&
        Array.isArray(linkedInspectionsResponse.data)
      ) {
        const data = linkedInspectionsResponse.data as LinkedInspection[];
        setLinkedInspections(data);
        setHasLinkedInspections(data.length > 0);
      } else {
        setLinkedInspections([]);
        setHasLinkedInspections(false);
      }
    } catch (e) {
      console.error(e);
      setLinkedInspections([]);
      setHasLinkedInspections(false);
    }

    setShowLoading(false);
  };

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />
      <PageHeader title={displayEquipment} showBack />
      <ApiError errorData={errorData} />
      <IonContent>
        <IonCard>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                {fields?.map((field) => {
                  let data = equipment?.[field.PropertyName as keyof Equipment];
                  if (field.NetType === "DateTime") {
                    const formattedString = new Date(data).toLocaleDateString();
                    data = formattedString;
                  }
                  return (
                    <IonCol key={field.ColumnName} size="6">
                      <IonRow>{field.TranslatedIdText}</IonRow>
                      <IonRow>
                        <IonLabel
                          style={{ fontWeight: "bold", wordBreak: "break-all" }}
                          class="ion-text-wrap"
                        >
                          {data || "-"}
                        </IonLabel>
                      </IonRow>
                    </IonCol>
                  );
                })}
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>
        <IonList lines="full">
          {hasCreateWorkOrderAccess && (
            <IonItem
              button
              detail
              routerLink={getCreateWorkOrderRoute(
                isRequester,
                displayEquipment
              )}
            >
              {translations["lbl_create_work_order"] ||
                "Create Work Order/Request"}
            </IonItem>
          )}
          <IonItem
            button
            detail
            routerLink={getOpenWorkOrdersRoute(isRequester, displayEquipment)}
          >
            {translations["lbl_open_orders"] || "Open Orders"}
          </IonItem>
          {hasHierarchyAccess && (
            <IonItem
              button
              detail
              routerLink={`/Equipment/${encodeParam(
                displayEquipment
              )}/hierarchy`}
            >
              {translations["lbl_hierarchy"] || "Hierarchy"}
            </IonItem>
          )}
          {hasDocumentAccess && (
            <IonItem
              button
              detail
              routerLink={`/Equipment/${encodeParam(
                displayEquipment
              )}/documents`}
            >
              {translations["lbl_view_documents"] || "View Documents"}
            </IonItem>
          )}
          {hasLinkedInspections && (
            <IonItem
              button
              detail
              onClick={() => {
                if (linkedInspections.length === 1) {
                  const encodedEq = encodeParam(displayEquipment)
                  const singleInspectionId =
                    linkedInspections[0].InspectionId?.toString() ?? "";
                  if (!singleInspectionId) {
                    // Fallback to list if id is missing
                    history.push(`/Equipment/${encodedEq}/inspections`);
                    return;
                  }
                  history.push(
                    `/Equipment/${encodedEq}/inspection/${singleInspectionId}`
                  );
                } else {
                  history.push(
                    `/Equipment/${encodeParam(displayEquipment)}/inspections`
                  );
                }
              }}
            >
              {translations["lbl_execute_inspections"] || "Execute Inspections"}
            </IonItem>
          )}
        </IonList>
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  EquipmentOverview,
  "EquipmentOverview"
);
